import React, { useState } from "react";
import axios from "axios";
import Papa from "papaparse";
import ReactPaginate from "react-paginate";
import "./style.css";

function App() {
  const apiBaseUrl = process.env.REACT_APP_WEBAPP_API_URL;
  const apiEnpointUrl = apiBaseUrl + "/api/SemRushHttpFunction";
  const PER_PAGE = process.env.REACT_APP_ROWS_PER_PAGE
    ? parseInt(process.env.REACT_APP_ROWS_PER_PAGE)
    : 50;

  const [activeTab, setActiveTab] = useState("daily");
  const [tabInputs, setTabInputs] = useState({
    daily: "",
    weekly: "",
    monthly: "",
  });
  const [tabData, setTabData] = useState({
    daily: [],
    weekly: [],
    monthly: [],
  });
  const [currentPages, setCurrentPages] = useState({
    daily: 0,
    weekly: 0,
    monthly: 0,
  });
  const [loadingStates, setLoadingStates] = useState({
    daily: false,
    weekly: false,
    monthly: false,
  });
  const [errors, setErrors] = useState({
    daily: "",
    weekly: "",
    monthly: "",
  });

  const tabConfig = {
    daily: {
      label: "Daily Stats",
      placeholder: "Enter up to 200 domains for last 24 hours stats",
      description: "Get the daily stats",
    },
    weekly: {
      label: "Weekly Stats",
      placeholder: "Enter up to 200 domains for last week stats",
      description: "Get the weekly stats",
    },
    monthly: {
      label: "Monthly Stats",
      placeholder: "Enter up to 200 domains for last month stats",
      description: "Get the monthly stats",
    },
  };

  const validateDomain = (domain) => {
    const pattern = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;
    return pattern.test(domain.trim());
  };

  const cleanDomain = (domain) => {
    let cleaned = domain.trim();
    cleaned = cleaned.replace(/^https?:\/\//, ""); // Remove protocol
    cleaned = cleaned.replace(/^www\./, ""); // Remove www.
    cleaned = cleaned.split("/")[0]; // Remove path
    return cleaned.toLowerCase();
  };

  // ---- Number formatting helpers ----
  const formatNumber = (value) => {
    if (value === undefined || value === null || value === "" || value === "-")
      return value;
    // Keep non-numeric strings like "Error" or "No data" as-is
    const num = Number(value);
    return isNaN(num) ? value : num.toLocaleString();
  };

  const numericKeys = [
    "visits",
    "users",
    "direct",
    "referral",
    "social",
    "search_organic",
    "social_organic",
  ];

  const formatRowForCSV = (row) => {
    const out = { ...row };
    numericKeys.forEach((k) => {
      if (k in out) out[k] = formatNumber(out[k]);
    });
    return out;
  };
  // -----------------------------------

  const handleInputChange = (tabType, value) => {
    setTabInputs((prev) => ({
      ...prev,
      [tabType]: value,
    }));
  };

  const handleSubmit = async (e, tabType) => {
    e.preventDefault();

    // Clear previous error and data for this tab
    setErrors((prev) => ({ ...prev, [tabType]: "" }));
    setTabData((prev) => ({ ...prev, [tabType]: [] }));
    setCurrentPages((prev) => ({ ...prev, [tabType]: 0 }));

    const input = tabInputs[tabType];
    const domainList = input
      .split(/[\n,]+/)
      .map((d) => cleanDomain(d))
      .filter((d) => d.length > 0);

    if (domainList.length > 200) {
      setErrors((prev) => ({
        ...prev,
        [tabType]: "You can only check up to 200 domains at once.",
      }));
      return;
    }

    const validDomains = domainList.filter(validateDomain);
    const invalidDomains = domainList.filter((d) => !validateDomain(d));

    if (invalidDomains.length > 0) {
      setErrors((prev) => ({
        ...prev,
        [tabType]: `Some domains are invalid: ${invalidDomains
          .slice(0, 5)
          .join(", ")}${invalidDomains.length > 5 ? "..." : ""}`,
      }));
      return;
    }

    setLoadingStates((prev) => ({ ...prev, [tabType]: true }));

    try {
      console.log(`Making request for ${tabType} with domains:`, validDomains);
      console.log(`Request payload:`, {
        domains: validDomains,
        endpoint_type: tabType,
      });

      console.log(`Using API URL: ${apiEnpointUrl}`);

      const res = await axios.post(
        apiEnpointUrl,
        {
          domains: validDomains,
          endpoint_type: tabType,
        },
        {
          timeout: 30000,
          headers: { "Content-Type": "application/json" },
        }
      );

      console.log(`Response for ${tabType}:`, res.data);

      if (res.data && res.data.results) {
        if (res.data.results.length === 0) {
          setErrors((prev) => ({
            ...prev,
            [tabType]: "No data found for the provided domains.",
          }));
        } else {
          setTabData((prev) => ({
            ...prev,
            [tabType]: res.data.results,
          }));
        }
      } else {
        console.error(`Invalid response structure for ${tabType}:`, res.data);
        setErrors((prev) => ({
          ...prev,
          [tabType]: `No data returned from backend. Response: ${JSON.stringify(
            res.data
          ).substring(0, 200)}`,
        }));
      }

      if (res.data && res.data.errors && res.data.errors.length > 0) {
        console.warn(`Backend errors for ${tabType}:`, res.data.errors);
        const errorMessages = res.data.errors
          .map((err) => `${err.domain}: ${err.error}`)
          .join(", ");
        setErrors((prev) => ({
          ...prev,
          [tabType]:
            prev[tabType] +
            (prev[tabType] ? " " : "") +
            `Some domains had errors: ${errorMessages.substring(0, 200)}${
              errorMessages.length > 200 ? "..." : ""
            }`,
        }));
      }
    } catch (err) {
      console.error(`Error for ${tabType}:`, err);
      let errorMessage = "Error fetching data: ";
      if (err.response) {
        errorMessage += `Server error ${err.response.status}: ${
          err.response.data?.detail || err.response.statusText
        }`;
        console.error(`Server response for ${tabType}:`, err.response.data);
      } else if (err.request) {
        errorMessage +=
          "No response from server. Check if the backend is running on " +
          apiBaseUrl;
      } else {
        errorMessage += err.message;
      }
      setErrors((prev) => ({ ...prev, [tabType]: errorMessage }));
    }

    setLoadingStates((prev) => ({ ...prev, [tabType]: false }));
  };

  const handleDownloadCSV = (tabType) => {
    const data = tabData[tabType];
    if (data.length === 0) return;

    // Apply number formatting to each row before exporting
    const formatted = data.map(formatRowForCSV);
    const csv = Papa.unparse(formatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `semrush_${tabType}_data.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const handlePageChange = (tabType, selected) => {
    setCurrentPages((prev) => ({ ...prev, [tabType]: selected }));
  };

  const getCurrentPageData = (tabType) => {
    const data = tabData[tabType];
    const currentPage = currentPages[tabType];
    const offset = currentPage * PER_PAGE;
    return data.slice(offset, offset + PER_PAGE);
  };

  const getPageCount = (tabType) => {
    return Math.ceil(tabData[tabType].length / PER_PAGE);
  };

  // Debug function to test backend connectivity
  const testBackendConnection = async () => {
    try {
      const response = await axios.get(apiEnpointUrl, { timeout: 5000 });
      console.log("Backend connection test:", response);
    } catch (err) {
      console.error("Backend connection failed:", err);
    }
  };

  const TestButton = () => (
    <button
      onClick={testBackendConnection}
      style={{
        position: "fixed",
        top: "10px",
        right: "10px",
        padding: "5px 10px",
        background: "#007bff",
        color: "white",
        border: "none",
        borderRadius: "3px",
        cursor: "pointer",
        fontSize: "12px",
        zIndex: 1000,
      }}
    >
      Test Backend
    </button>
  );

  return (
    <div className="main-container">
      <TestButton />

      <div className="header">
        <img
          src="https://www.blazeo.com/wp-content/uploads/2025/02/blazeo-logo-formerly-apexchat.png"
          className="logo"
          alt="Blazeo Logo"
        />
        <h1>SEMrush Domain Analytics</h1>
        <p>
          Analyze domain traffic data with daily, weekly, and monthly insights
        </p>
      </div>

      <div className="tab-container">
        <div className="tab-buttons">
          {Object.entries(tabConfig).map(([key, config]) => (
            <button
              key={key}
              className={`tab-button ${activeTab === key ? "active" : ""}`}
              onClick={() => setActiveTab(key)}
            >
              {config.label}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {Object.entries(tabConfig).map(([tabType, config]) => (
            <div
              key={tabType}
              className={`tab-panel ${activeTab === tabType ? "active" : ""}`}
            >
              <div className="form-section">
                <h3>{config.description}</h3>
                <form onSubmit={(e) => handleSubmit(e, tabType)}>
                  <div className="textarea-container">
                    <textarea
                      rows="6"
                      value={tabInputs[tabType]}
                      placeholder={config.placeholder}
                      onChange={(e) =>
                        handleInputChange(tabType, e.target.value)
                      }
                      disabled={loadingStates[tabType]}
                    />
                  </div>
                  <button
                    type="submit"
                    className={`submit-button ${
                      loadingStates[tabType] ? "loading" : ""
                    }`}
                    disabled={loadingStates[tabType]}
                  >
                    {loadingStates[tabType] ? (
                      <>
                        <div className="spinner"></div>
                        Fetching Data...
                      </>
                    ) : (
                      "Analyze Domains"
                    )}
                  </button>
                </form>

                {errors[tabType] && (
                  <div className="error-message">{errors[tabType]}</div>
                )}
              </div>

              {tabData[tabType].length > 0 && (
                <div className="results-section">
                  <div className="results-header">
                    <h3>Results ({tabData[tabType].length} domains)</h3>
                    <button
                      onClick={() => handleDownloadCSV(tabType)}
                      className="download-button"
                    >
                      Download CSV
                    </button>
                  </div>

                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Domain</th>
                          <th>Visits</th>
                          <th>Users</th>
                          <th>Direct</th>
                          <th>Referral</th>
                          <th>Social</th>
                          <th>Search Organic</th>
                          <th>Social Organic</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getCurrentPageData(tabType).map((row, idx) => (
                          <tr key={idx}>
                            <td className="domain-cell">{row.target}</td>
                            <td>{formatNumber(row.visits)}</td>
                            <td>{formatNumber(row.users)}</td>
                            <td>{formatNumber(row.direct)}</td>
                            <td>{formatNumber(row.referral)}</td>
                            <td>{formatNumber(row.social)}</td>
                            <td>{formatNumber(row.search_organic)}</td>
                            <td>{formatNumber(row.social_organic)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {getPageCount(tabType) > 1 && (
                    <ReactPaginate
                      pageCount={getPageCount(tabType)}
                      onPageChange={({ selected }) =>
                        handlePageChange(tabType, selected)
                      }
                      containerClassName="pagination"
                      activeClassName="active"
                      pageRangeDisplayed={3}
                      marginPagesDisplayed={1}
                      previousLabel="‹"
                      nextLabel="›"
                      breakLabel="..."
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default App;
